// Occasion catalog — single source of truth for occasion + subcategory
// vocabulary. Replaces the ad-hoc duplicate enums that previously
// lived in:
//
//   - src/util/listingHelpers.js (EVENT_TYPE_LABELS, CATEGORY_EMOJIS,
//     ADJACENT_OCCASIONS)
//   - src/containers/EditListingPage/EditListingWizard/EditListingBasicsPanel/
//     EditListingBasicsForm.js (EVENT_TYPES, SUB_CATEGORIES)
//   - src/containers/RentEventHomePage/CategoryGridSection (categories)
//   - src/containers/RentEventHomePage/HeroSearchSection (CATEGORY_PILLS
//     + the <select> options)
//   - src/data/sub-themes.json
//
// All of those now read from this file. Anything that needs the
// list of occasions or any subcategory list reads from here; the
// legacy ad-hoc tables silently dropped chips when the lists
// drifted out of sync.
//
// **Consolidation 2026-05-15:**
//   - cultural folded into holiday (Diwali, Lunar NY, Día de los
//     Muertos, etc. now live under Holiday)
//   - beach_day broadened to outdoor (Beach BBQ, Backyard BBQ,
//     pool, picnic, etc.)
//   - themed_dinners + game_day merged into the new
//     dinner_parties_gatherings (book club, wine tasting, game night,
//     Friendsgiving, etc.)
//
// Existing listings tagged with the legacy keys (cultural,
// beach_day, themed_dinners, game_day, kids_birthday) keep working
// via the LEGACY_OCCASION_ALIASES map below — the read-side
// resolves them to the new keys. A one-time backfill script
// (PR-B) rewrites them on the listing side; once that runs and
// verifies clean, the alias map can be retired.

export const OCCASIONS = [
  {
    key: 'birthday',
    label: 'Birthday',
    emoji: '🎂',
    description: "Kids', milestone, princess, dinosaur, superhero & more",
    // Event-types only. The decorative THEMES that used to live here
    // (princess/fairy tale, dinosaur, superhero, sports-/character-themed,
    // pastel/floral/girly, tropical/luau, vintage/retro) were removed —
    // themes belong in the open-ended keywords field, not the structured
    // sub-category taxonomy.
    subcategories: [
      { key: 'kids_birthday', label: "Kids' birthday" },
      { key: 'adult_birthday', label: 'Adult birthday' },
      { key: 'milestone_birthday', label: 'Milestone birthday' },
      { key: 'first_birthday', label: 'First birthday' },
    ],
  },
  {
    key: 'bridal',
    label: 'Bridal & Wedding',
    emoji: '💍',
    description: 'Showers, bachelorettes, rehearsal dinners',
    subcategories: [
      { key: 'bridal_shower', label: 'Bridal shower' },
      { key: 'bachelorette', label: 'Bachelorette party' },
      { key: 'engagement_party', label: 'Engagement party' },
      { key: 'rehearsal_dinner', label: 'Rehearsal dinner' },
      { key: 'wedding_shower', label: 'Wedding shower' },
      { key: 'day_of_wedding_decor', label: 'Day-of wedding decor' },
      { key: 'welcome_dinner', label: 'Welcome dinner' },
      { key: 'day_after_brunch', label: 'Day-after brunch' },
    ],
  },
  {
    key: 'baby_shower',
    label: 'Baby Shower',
    emoji: '👶',
    description: 'Sprinkles, gender reveals, sip & sees',
    subcategories: [
      { key: 'traditional_baby_shower', label: 'Traditional baby shower' },
      { key: 'sprinkle', label: 'Sprinkle' },
      { key: 'gender_reveal', label: 'Gender reveal' },
      { key: 'sip_and_see', label: 'Sip & see' },
      { key: 'diaper_party', label: 'Diaper party / dad shower' },
    ],
  },
  {
    key: 'holiday',
    label: 'Holiday',
    emoji: '🎃',
    description: 'Halloween, Christmas, Diwali, Lunar New Year & more',
    subcategories: [
      { key: 'halloween', label: 'Halloween' },
      { key: 'thanksgiving', label: 'Thanksgiving' },
      { key: 'christmas', label: 'Christmas' },
      { key: 'hanukkah', label: 'Hanukkah' },
      { key: 'new_years_eve', label: "New Year's Eve" },
      { key: 'valentines_day', label: "Valentine's Day" },
      { key: 'easter', label: 'Easter' },
      { key: 'fourth_of_july', label: '4th of July' },
      { key: 'mothers_fathers_day', label: "Mother's Day / Father's Day" },
      { key: 'diwali', label: 'Diwali' },
      { key: 'lunar_new_year', label: 'Lunar New Year' },
      { key: 'dia_de_los_muertos', label: 'Día de los Muertos' },
      { key: 'passover', label: 'Passover' },
      { key: 'eid', label: 'Eid' },
    ],
  },
  {
    key: 'milestone',
    label: 'Milestone',
    emoji: '🎓',
    description: 'Graduation, retirement, sweet 16, anniversaries',
    subcategories: [
      { key: 'graduation', label: 'Graduation' },
      { key: 'retirement', label: 'Retirement' },
      { key: 'anniversary', label: 'Anniversary' },
      { key: 'sweet_16', label: 'Sweet 16' },
      { key: 'quinceanera', label: 'Quinceañera' },
      { key: 'bar_bat_mitzvah', label: 'Bar / Bat Mitzvah' },
      { key: 'housewarming', label: 'Housewarming' },
      { key: 'promotion_new_job', label: 'Promotion / new job' },
      { key: 'welcome_home_going_away', label: 'Welcome home / going away' },
      { key: 'proposal', label: 'Proposal' },
    ],
  },
  {
    key: 'outdoor',
    label: 'Outdoor',
    emoji: '🌳',
    description: 'Backyard BBQ, garden party, pool, picnic',
    subcategories: [
      { key: 'beach_bbq', label: 'Beach BBQ' },
      { key: 'backyard_bbq', label: 'Backyard BBQ' },
      { key: 'garden_party', label: 'Garden party' },
      { key: 'pool_party', label: 'Pool party' },
      { key: 'picnic', label: 'Picnic' },
      { key: 'bonfire_smores_night', label: "Bonfire / s'mores night" },
      { key: 'outdoor_movie_night', label: 'Outdoor movie night' },
      { key: 'tailgate', label: 'Tailgate' },
      { key: 'block_party', label: 'Block party' },
    ],
  },
  {
    key: 'dinner_parties_gatherings',
    label: 'Dinner Parties & Gatherings',
    emoji: '🍽️',
    description: 'Dinner parties, wine tasting, book club, brunch',
    subcategories: [
      { key: 'dinner_party', label: 'Dinner party' },
      { key: 'wine_tasting', label: 'Wine tasting' },
      { key: 'cocktail_hour', label: 'Cocktail hour' },
      { key: 'brunch', label: 'Brunch' },
      { key: 'book_club', label: 'Book club' },
      { key: 'game_night', label: 'Game night' },
      { key: 'tea_party', label: 'Tea party' },
      { key: 'coffee_and_dessert', label: 'Coffee & dessert' },
      { key: 'friendsgiving', label: 'Friendsgiving' },
      { key: 'sunday_supper', label: 'Sunday supper' },
      { key: 'date_night', label: 'Date night' },
      { key: 'proposal', label: 'Proposal' },
    ],
  },
  {
    key: 'themed_parties',
    label: 'Themed Parties',
    emoji: '🎭',
    description: 'Murder mystery, decade nights, masquerade, Gatsby',
    subcategories: [
      { key: 'murder_mystery', label: 'Murder mystery' },
      { key: 'costume_party', label: 'Costume party' },
      { key: 'decade_night', label: 'Decade night' },
      { key: 'disco_dance_party', label: 'Disco / dance party' },
      { key: 'masquerade', label: 'Masquerade' },
      { key: 'casino_night', label: 'Casino night' },
      { key: 'gatsby', label: 'Roaring 20s / Gatsby' },
      { key: 'hollywood_red_carpet', label: 'Hollywood / red carpet' },
      { key: 'western_cowboy', label: 'Western / cowboy' },
      { key: 'black_tie', label: 'Black tie' },
      { key: 'karaoke_night', label: 'Karaoke night' },
    ],
  },
];

export const OCCASION_KEYS = OCCASIONS.map(o => o.key);

// Legacy → new mapping. Used both by the read-side (display
// fallback) and by the one-time backfill script. Once the backfill
// runs verify-clean, this map becomes dead code and can be
// removed in a follow-up PR.
export const LEGACY_OCCASION_ALIASES = {
  cultural: 'holiday',
  beach_day: 'outdoor',
  themed_dinners: 'dinner_parties_gatherings',
  game_day: 'dinner_parties_gatherings',
};

/**
 * Resolve a possibly-legacy occasion key to its canonical form.
 * Unknown keys (typos, future schema) fall through unchanged so
 * downstream code can decide how to handle them.
 */
export const resolveOccasion = key => LEGACY_OCCASION_ALIASES[key] || key;

/**
 * Occasion key from a raw Sharetribe query value — strips the
 * `has_any:`/`has_all:` operator prefix, takes the first key of a
 * comma list, and resolves legacy aliases. Shared by the search
 * header, empty states, and anything else reading pub_occasions
 * straight from the URL.
 */
export const occasionKeyFromQueryValue = value =>
  value
    ? resolveOccasion(
        String(value)
          .replace(/^has_(any|all):/, '')
          .split(',')[0]
          .trim()
      )
    : null;

/**
 * Find an occasion by key. Resolves legacy aliases first so a
 * listing tagged `cultural` returns the Holiday entry. Returns
 * null for unknown keys.
 */
export const findOccasion = key => {
  if (!key || typeof key !== 'string') return null;
  const resolved = resolveOccasion(key);
  return OCCASIONS.find(o => o.key === resolved) || null;
};

/**
 * Find a subcategory entry by (occasionKey, subKey). Returns null
 * if either lookup misses. Occasion is alias-resolved first.
 */
export const findSubcategory = (occasionKey, subKey) => {
  const occasion = findOccasion(occasionKey);
  if (!occasion || !subKey) return null;
  return occasion.subcategories.find(s => s.key === subKey) || null;
};

/**
 * Subcategories array for a given occasion key (alias-resolved).
 * Empty array for unknown / missing occasions.
 */
export const subcategoriesFor = occasionKey => {
  const occasion = findOccasion(occasionKey);
  return occasion ? occasion.subcategories : [];
};

/**
 * Render-friendly label for an occasion key. Resolves legacy
 * aliases before lookup. Falls through to the raw key for
 * truly-unknown values so display never breaks.
 */
export const occasionLabel = key => {
  const occasion = findOccasion(key);
  return occasion ? occasion.label : key || '';
};

/**
 * Render-friendly label for a subcategory key under a given
 * occasion. Defensive against the case where a listing's saved
 * sub_categories don't match the active occasion (drift from a
 * pre-migration era) — returns the raw key as a label fallback.
 */
export const subcategoryLabel = (occasionKey, subKey) => {
  if (!subKey) return '';
  const entry = findSubcategory(occasionKey, subKey);
  if (entry) return entry.label;
  // Try every occasion (the saved sub may belong to a different
  // top-level than the listing's current event_type).
  for (const occasion of OCCASIONS) {
    const hit = occasion.subcategories.find(s => s.key === subKey);
    if (hit) return hit.label;
  }
  return subKey;
};

/**
 * Curated "browse adjacent occasions" mapping for the no-results
 * empty state (Stream 3 PR-5). Updated for the consolidated
 * 8-occasion list.
 */
export const ADJACENT_OCCASIONS = {
  birthday: ['milestone', 'themed_parties', 'holiday'],
  bridal: ['baby_shower', 'dinner_parties_gatherings', 'milestone'],
  baby_shower: ['bridal', 'dinner_parties_gatherings', 'birthday'],
  holiday: ['dinner_parties_gatherings', 'themed_parties', 'milestone'],
  milestone: ['birthday', 'bridal', 'dinner_parties_gatherings'],
  outdoor: ['dinner_parties_gatherings', 'themed_parties', 'holiday'],
  dinner_parties_gatherings: ['bridal', 'holiday', 'outdoor'],
  themed_parties: ['birthday', 'holiday', 'dinner_parties_gatherings'],
};
